import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { ChildInformation, SpouseInformation, HomeAddressData } from '../../../types/customer-portal';
import DatePicker from '../../../../../shared/components/ui/DatePicker/DatePicker';
import SearchableDropdown from '../../../../../components/common/SearchableDropdown';
import { dateUtils } from '../../../../../shared/utils/dateUtils';
import { useLocalAddressesProvinces } from '../../../hooks/useLocalAddressesProvinces';
import { useLocalAddressesCities } from '../../../hooks/useLocalAddressesCities';
import { useLocalAddressesBarangays } from '../../../hooks/useLocalAddressesBarangays';
import { getProvinceCodeFromDescription } from '../../../utils/provinceMapper';
import { getZipCodeFromBarangay, shouldAutoPopulateZipCode } from '../../../utils/barangayZipCodeUtils';
import {
  validateNameField,
  filterNameField,
  validateAddressField,
  filterAddressField,
  NAME_FIELD_MAX_LENGTH,
  ADDRESS_FIELD_MAX_LENGTH
} from '../../../../../shared/utils/fieldValidation';
import './ChildInformationModal.css';
import '../../../styles/zipCodeAutoPopulation.css';

interface ChildInformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (child: ChildInformation) => void;
  onDelete?: (childId: string) => void;
  child?: ChildInformation | null;
  isEditMode?: boolean;
  spouseAddress?: SpouseInformation | null; // For address sharing (backward compatibility)
  principalHomeAddress?: HomeAddressData | null; // NEW: Principal Insured home address
}

const ChildInformationModal: React.FC<ChildInformationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  child,
  isEditMode = false,
  spouseAddress,
  principalHomeAddress
}) => {
  const [formData, setFormData] = useState<Omit<ChildInformation, 'id'>>({
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: 'N/A',
    dateOfBirth: '',
    age: '',
    gender: '',
    shareAddressWithPrincipal: false, // NEW: Share address with Principal Insured
    houseUnitNo: '', // Address field kept for data structure
    streetName: '', // Address field kept for data structure
    subdivisionVillage: '', // Address field kept for data structure
    barangay: '', // Address field kept for data structure
    city: '', // Address field kept for data structure
    province: '', // Address field kept for data structure
    zipCode: '', // Address field kept for data structure
    country: 'Philippines, Singapore, Thailand'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Omit<ChildInformation, 'id'> | null>(null);
  
  // Field validation highlighting state (like SpouseInformationModal)
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  // Zip code auto-population state
  const [isZipCodeAutoPopulated, setIsZipCodeAutoPopulated] = useState(false);
  
  // Use the local addresses provinces hook
  const { provinces, provinceOptions, loading: provincesLoading, error: provincesError} = useLocalAddressesProvinces();
  
  // Get selected province code for city filtering
  const selectedProvinceCode = useMemo(() => {
    return getProvinceCodeFromDescription(provinces, formData.province || '');
  }, [provinces, formData.province]);

  // Use the local addresses cities hook with province filtering
  const { cityOptions, loading: citiesLoading, error: citiesError } = useLocalAddressesCities(selectedProvinceCode);

  // Use the local addresses barangays hook with city filtering
  const { barangays, barangayOptions, loading: barangaysLoading, error: barangaysError } = useLocalAddressesBarangays(formData.city);

  // Reset form when modal opens/closes or child changes
  useEffect(() => {
    if (isOpen) {
      if (child && isEditMode) {
        const initialData = {
          title: child.title,
          firstName: child.firstName,
          middleName: child.middleName || '',
          lastName: child.lastName,
          suffix: child.suffix,
          dateOfBirth: child.dateOfBirth,
          age: child.age,
          gender: child.gender,
          shareAddressWithPrincipal: child.shareAddressWithPrincipal || false, // NEW: Handle new field
          houseUnitNo: child.houseUnitNo, // Address field kept for data structure
          streetName: child.streetName, // Address field kept for data structure
          subdivisionVillage: child.subdivisionVillage, // Address field kept for data structure
          barangay: child.barangay, // Address field kept for data structure
          city: child.city, // Address field kept for data structure
          province: child.province, // Address field kept for data structure
          zipCode: child.zipCode, // Address field kept for data structure
          country: child.country
        };
        setFormData(initialData);
        setOriginalFormData(initialData);
        setHasFormChanges(false);
      } else {
        // Address sharing logic restored with enhanced debugging
        const shouldUseSpouseAddress = spouseAddress?.shareAddressWithChild === true;
        
        // Debug logging to track address sharing
        console.log('ChildInformationModal - Address sharing debug:', {
          spouseAddress: spouseAddress,
          shareAddressWithChild: spouseAddress?.shareAddressWithChild,
          shouldUseSpouseAddress: shouldUseSpouseAddress,
          spouseHouseUnitNo: spouseAddress?.houseUnitNo,
          spouseBarangay: spouseAddress?.barangay,
          spouseCity: spouseAddress?.city,
          spouseProvince: spouseAddress?.province,
          spouseZipCode: spouseAddress?.zipCode
        });
        
        const initialData = {
          title: '',
          firstName: '',
          middleName: '',
          lastName: '',
          suffix: 'N/A',
          dateOfBirth: '',
          age: '',
          gender: '',
          shareAddressWithPrincipal: false, // NEW: Initialize new field
          houseUnitNo: shouldUseSpouseAddress ? (spouseAddress.houseUnitNo || '') : '',
          streetName: shouldUseSpouseAddress ? (spouseAddress.streetName || '') : '',
          subdivisionVillage: shouldUseSpouseAddress ? (spouseAddress.subdivisionVillage || '') : '',
          barangay: shouldUseSpouseAddress ? (spouseAddress.barangay || '') : '',
          city: shouldUseSpouseAddress ? (spouseAddress.city || '') : '',
          province: shouldUseSpouseAddress ? (spouseAddress.province || '') : '',
          zipCode: shouldUseSpouseAddress ? (spouseAddress.zipCode || '') : '',
          country: shouldUseSpouseAddress ? (spouseAddress.country || 'Philippines') : 'Philippines'
        };
        
        console.log('ChildInformationModal - Initial data populated:', initialData);
        
        setFormData(initialData);
        setOriginalFormData(null);
        setHasFormChanges(false);
      }
      setErrors({});
    }
  }, [isOpen, child, isEditMode, spouseAddress]);

  // Handle dynamic spouse address sharing updates
  useEffect(() => {
    // Only update if modal is open, not in edit mode, and spouse address sharing is enabled
    if (isOpen && !isEditMode && spouseAddress?.shareAddressWithChild === true) {
      console.log('ChildInformationModal - Spouse address sharing enabled, updating form data dynamically');
      setFormData(prev => ({
        ...prev,
        houseUnitNo: spouseAddress.houseUnitNo || '',
        streetName: spouseAddress.streetName || '',
        subdivisionVillage: spouseAddress.subdivisionVillage || '',
        barangay: spouseAddress.barangay || '',
        city: spouseAddress.city || '',
        province: spouseAddress.province || '',
        zipCode: spouseAddress.zipCode || '',
        country: spouseAddress.country || 'Philippines'
      }));
    }
  }, [
    spouseAddress?.shareAddressWithChild,
    spouseAddress?.houseUnitNo,
    spouseAddress?.streetName,
    spouseAddress?.subdivisionVillage,
    spouseAddress?.barangay,
    spouseAddress?.city,
    spouseAddress?.province,
    spouseAddress?.zipCode,
    spouseAddress?.country,
    isOpen,
    isEditMode
  ]);

  // NEW: Handle Principal Insured address auto-population
  useEffect(() => {
    // Only update if modal is open and not in edit mode
    if (isOpen && !isEditMode && principalHomeAddress) {
      if (formData.shareAddressWithPrincipal === true) {
        console.log('ChildInformationModal - Principal address sharing enabled, updating form data');
        // Combine houseUnitNo and streetName from home address for the address field
        const combinedAddress = [
          principalHomeAddress.houseUnitNo || '',
          principalHomeAddress.streetName || ''
        ].filter(Boolean).join(' ').trim();
        
        setFormData(prev => ({
          ...prev,
          houseUnitNo: combinedAddress || principalHomeAddress.houseUnitNo || '',
          streetName: principalHomeAddress.streetName || '',
          subdivisionVillage: principalHomeAddress.subdivisionVillage || '',
          barangay: principalHomeAddress.barangay || '',
          city: principalHomeAddress.city || '',
          province: principalHomeAddress.province || '',
          zipCode: principalHomeAddress.zipCode || '',
          country: principalHomeAddress.country || 'Philippines'
        }));
      } else if (formData.shareAddressWithPrincipal === false) {
        console.log('ChildInformationModal - Principal address sharing disabled, clearing address fields');
        setFormData(prev => ({
          ...prev,
          houseUnitNo: '',
          streetName: '',
          subdivisionVillage: '',
          barangay: '',
          city: '',
          province: '',
          zipCode: '',
          country: 'Philippines'
        }));
        setIsZipCodeAutoPopulated(false);
      }
    }
  }, [
    formData.shareAddressWithPrincipal,
    isOpen,
    isEditMode,
    principalHomeAddress
  ]);

  // Calculate age when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      const age = dateUtils.calculateAge(new Date(formData.dateOfBirth));
      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dateOfBirth]);

  // Detect form changes for save button state
  useEffect(() => {
    if (isEditMode && originalFormData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setHasFormChanges(hasChanges);
    } else {
      setHasFormChanges(false);
    }
  }, [formData, originalFormData, isEditMode]);

  // Helper functions for field validation (like SpouseInformationModal)
  const getValidationErrors = (): Record<string, boolean> => {
    const errors: Record<string, boolean> = {};
    
    // Required fields validation
    if (!formData.title?.trim()) errors.title = true;
    if (!formData.firstName?.trim()) errors.firstName = true;
    if (!formData.lastName?.trim()) errors.lastName = true;
    if (!formData.dateOfBirth?.trim()) errors.dateOfBirth = true;
    if (!formData.gender || formData.gender === 'Choose Gender') errors.gender = true;
    if (!formData.houseUnitNo?.trim()) errors.houseUnitNo = true;
    if (!formData.barangay?.trim()) errors.barangay = true;
    if (!formData.city?.trim()) errors.city = true;
    if (!formData.province?.trim()) errors.province = true;
    if (!formData.zipCode?.trim()) errors.zipCode = true;
    
    // Add pattern validation for name fields
    if (formData.firstName && !validateNameField(formData.firstName)) errors.firstName = true;
    if (formData.lastName && !validateNameField(formData.lastName)) errors.lastName = true;
    if (formData.middleName && !validateNameField(formData.middleName)) errors.middleName = true;
    
    // Add pattern validation for address fields
    if (formData.houseUnitNo && !validateAddressField(formData.houseUnitNo)) errors.houseUnitNo = true;
    if (formData.streetName && !validateAddressField(formData.streetName)) errors.streetName = true;
    if (formData.subdivisionVillage && !validateAddressField(formData.subdivisionVillage)) errors.subdivisionVillage = true;
    
    return errors;
  };

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? 'field-error' : '';
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleInputChange = (field: keyof Omit<ChildInformation, 'id'>, value: string | boolean) => {
    // Prevent country field changes - always keep it as Philippines
    if (field === 'country') {
      return;
    }
    
    let processedValue = value;
    
    // Apply field-specific filtering for string fields
    if (typeof value === 'string') {
      if (field === 'firstName' || field === 'lastName' || field === 'middleName') {
        processedValue = filterNameField(value);
      } else if (field === 'houseUnitNo' || field === 'streetName' || field === 'subdivisionVillage') {
        processedValue = filterAddressField(value);
      }
    }
    
    setFormData(prev => {
      const newData = { ...prev, [field]: processedValue };
      
      // Clear city when province changes
      if (field === 'province' && typeof processedValue === 'string' && processedValue !== prev.province) {
        newData.city = '';
        newData.barangay = ''; // Also clear barangay when province changes
        newData.zipCode = ''; // Clear zip code when province changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Clear barangay when city changes
      if (field === 'city' && typeof processedValue === 'string' && processedValue !== prev.city) {
        newData.barangay = '';
        newData.zipCode = ''; // Clear zip code when city changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Auto-populate zip code when barangay is selected
      if (field === 'barangay' && typeof processedValue === 'string') {
        if (processedValue && shouldAutoPopulateZipCode(processedValue, barangays)) {
          const zipCode = getZipCodeFromBarangay(barangays, processedValue);
          if (zipCode) {
            newData.zipCode = zipCode;
            setIsZipCodeAutoPopulated(true);
          } else {
            // If no zip code found, allow manual entry
            setIsZipCodeAutoPopulated(false);
          }
        } else {
          // If barangay is cleared, reset zip code and auto-population flag
          newData.zipCode = '';
          setIsZipCodeAutoPopulated(false);
        }
      }
      
      return newData;
    });

    // Clear field error when user starts typing (new approach)
    clearFieldError(field);
    
    // Clear error when user starts typing (existing approach)
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        
        // Also clear city and barangay errors when province changes
        if (field === 'province') {
          delete newErrors.city;
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Also clear barangay error when city changes
        if (field === 'city') {
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Clear zip code error when barangay changes
        if (field === 'barangay') {
          delete newErrors.zipCode;
        }
        
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields validation (address fields restored)
    const requiredFields = [
      "title",
      "firstName",
      "lastName",
      "dateOfBirth",
      "gender",
      "houseUnitNo",
      "city",
      "province",
      "zipCode",
    ];

    requiredFields.forEach(field => {
      const value = formData[field as keyof typeof formData];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`;
      }
    });

    // Gender validation
    if (formData.gender === '' || formData.gender === 'Choose Gender') {
      newErrors.gender = 'Please select a gender';
    }

    // Age validation for children (0-17 years old)
    if (formData.dateOfBirth) {
      const birthDate = dateUtils.parseDate(formData.dateOfBirth);
      if (birthDate) {
        const age = dateUtils.calculateAge(birthDate);
        if (age < 0 || age > 17) {
          newErrors.dateOfBirth = 'Child must be between 0 and 17 years old';
        }
      } else {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    // Check for validation errors (new approach)
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;
    
    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      return; // Don't proceed if there are validation errors
    }
    
    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);
    
    // Existing validation
    if (!validateForm()) {
      return;
    }

    const childData: ChildInformation = {
      id: child?.id || Date.now().toString(),
      ...formData
    };

    onSave(childData);
    onClose();
  };

  const handleDelete = () => {
    if (child && onDelete) {
      onDelete(child.id);
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="child-modal-backdrop" onClick={handleBackdropClick}>
      <div className="child-modal-container">
        {/* Modal Content */}
        <div className="child-modal-content">
          <div className="child-modal-card">
            {/* All Content - Header, Form, and Footer are now scrollable */}
            <div className="child-modal-form-content">
              {/* Modal Header - Now inside scrollable area */}
              <div className="child-modal-form-header">
                <div className="child-modal-title-section">
                  <h2 className="child-modal-form-title">Child Information</h2>
                  <p className="child-modal-form-subtitle">
                    You can add up to 5 children
                  </p>
                </div>
                <button
                  className="child-modal-close"
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              <div className="child-form">
                {/* Name Fields Row */}
                <div className="form-row name-row">
                  <div
                    className={`form-group title-group ${getFieldErrorClass("title")}`}
                  >
                    <label className="form-label">Title *</label>
                    <select
                      className={`form-select ${errors.title ? 'error' : ''}`}
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                    >
                      <option value="">Title</option>                      
                      <option value="Ms.">Ms.</option>
                      <option value="Mr.">Mr.</option>
                    </select>
                  </div>

                  <div className={`form-group ${getFieldErrorClass('firstName')}`}>
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.firstName ? 'error' : ''}`}
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder=""
                      maxLength={NAME_FIELD_MAX_LENGTH}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Middle Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.middleName}
                      onChange={(e) => handleInputChange('middleName', e.target.value)}
                      placeholder=""
                      maxLength={NAME_FIELD_MAX_LENGTH}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('lastName')}`}>
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.lastName ? 'error' : ''}`}
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder=""
                      maxLength={NAME_FIELD_MAX_LENGTH}
                    />
                  </div>

                  <div className="form-group suffix-group">
                    <label className="form-label">Suffix</label>
                    <select
                      className="form-select"
                      value={formData.suffix}
                      onChange={(e) => handleInputChange('suffix', e.target.value)}
                    >
                      <option value="N/A">N/A</option>
                      <option value="Jr.">Jr.</option>
                      <option value="Sr.">Sr.</option>
                      <option value="II">II</option>
                      <option value="III">III</option>
                    </select>
                  </div>
                </div>

                {/* Date of Birth, Age, Gender Row */}
                <div className="form-row child-form-row-2nd">
                  <div className={`form-group ${getFieldErrorClass('dateOfBirth')}`}>
                    <label className="form-label">Date of Birth *</label>
                    <DatePicker
                      value={formData.dateOfBirth}
                      onChange={(value) => handleInputChange('dateOfBirth', value)}
                      placeholder="MM/DD/YYYY"
                      name="child-dateOfBirth"
                      className={`child-datepicker form-datepicker ${errors.dateOfBirth ? 'error' : ''}`}
                      position="above"
                      validationRules={dateUtils.createChildBirthdateRules()}
                      error={errors.dateOfBirth}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Age *</label>
                    <input
                      type="text"
                      className="form-input spouse-info-age-read-only"
                      value={formData.age}
                      readOnly
                      placeholder="Auto-calculated"
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('gender')}`}>
                    <label className="form-label">Gender *</label>
                    <select
                      className={`form-select ${errors.gender ? 'error' : ''}`}
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    >
                      <option value="">Choose Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </div>

                {/* Address Sharing Checkbox */}
                <div className="form-row child-checkbox-form-row">
                  <div className="form-group checkbox-group child-checkbox-group">
                    <label className="child-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.shareAddressWithPrincipal || false}
                        onChange={(e) => handleInputChange('shareAddressWithPrincipal', e.target.checked)}
                        className="checkbox-input child-checkbox-input"
                      />
                      <span className="checkbox-text">Do you and your child share the same home address?</span>
                    </label>
                  </div>
                </div>

                {/* ADDRESS FIELDS - RESTORED */}
                <div className="form-row child-form-row-3rd">
                  <div className={`form-group ${getFieldErrorClass('houseUnitNo')}`}>
                    <label className="form-label">House/Unit No., Street Name, Subdivision/Village *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.houseUnitNo ? 'error' : ''}`}
                      value={formData.houseUnitNo}
                      onChange={(e) => handleInputChange('houseUnitNo', e.target.value)}
                      placeholder="123"
                      maxLength={ADDRESS_FIELD_MAX_LENGTH}
                    />
                  </div>
                  <div className={`form-group ${getFieldErrorClass('province')}`}>
                    <label className="form-label">Province *</label>
                    <SearchableDropdown
                      options={provinceOptions}
                      selectedValue={formData.province}
                      onChange={(value) => handleInputChange('province', value)}
                      placeholder={provincesLoading ? "Loading provinces..." : "Select Province"}
                      searchPlaceholder="Search provinces..."
                      className={errors.province ? 'error' : ''}
                      error={!!errors.province}
                      position="below"
                      dropdownId="child-province"
                      disabled={provincesLoading || !!provincesError}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className={`form-group ${getFieldErrorClass('city')}`}>
                    <label className="form-label">City *</label>
                    <SearchableDropdown
                      options={cityOptions}
                      selectedValue={formData.city}
                      onChange={(value) => handleInputChange('city', value)}
                      placeholder={
                        !formData.province
                          ? "Select City"
                          : citiesLoading
                            ? "Loading cities..."
                            : "Select City"
                      }
                      searchPlaceholder="Search cities..."
                      className={errors.city ? 'error' : ''}
                      error={!!errors.city}
                      position="below"
                      dropdownId="child-city"
                      disabled={!formData.province || citiesLoading || !!citiesError}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('barangay')}`}>
                    <label className="form-label">Barangay *</label>
                    <SearchableDropdown
                      options={barangayOptions}
                      selectedValue={formData.barangay}
                      onChange={(value) => handleInputChange('barangay', value)}
                      placeholder={
                        !formData.city
                          ? "Select Barangay"
                          : barangaysLoading
                            ? "Loading barangays..."
                            : "Select Barangay"
                      }
                      searchPlaceholder="Search barangays..."
                      className={errors.barangay ? 'error' : ''}
                      error={!!errors.barangay}
                      position="below"
                      dropdownId="child-barangay"
                      disabled={!formData.city || barangaysLoading || !!barangaysError}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('zipCode')}`}>
                    <label className="form-label">Zip Code *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.zipCode ? 'error' : ''} ${isZipCodeAutoPopulated ? 'auto-populated' : ''}`}
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      placeholder={isZipCodeAutoPopulated ? "Auto-populated from barangay" : "1017"}
                      maxLength={4}
                      readOnly={isZipCodeAutoPopulated}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country *</label>
                    <SearchableDropdown
                      options={['Philippines']}
                      selectedValue="Philippines"
                      onChange={() => {}} // No-op function since it's disabled
                      placeholder="Philippines"
                      searchPlaceholder="Philippines"
                      className={`${errors.country ? 'error' : ''} disabled-country-field`}
                      error={!!errors.country}
                      position="below"
                      dropdownId="child-country"
                      disabled={true} // Always disabled
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer - Now inside scrollable content */}
              <div className="child-modal-footer">
                {isEditMode && onDelete ? (
                  <div className="child-modal-footer-left">
                    <button
                      className="child-modal-delete-btn"
                      onClick={handleDelete}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="delete-icon"
                      >
                        <path
                          d="M3 6H5H21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 11V17"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 11V17"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Delete Family Member
                    </button>
                    <div className='child-modal-delete-warning-position'>
                      <p className="child-modal-delete-warning">
                        By clicking this button it will permanently remove this as family member.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}

                <div className="child-modal-footer-actions">
                  <button
                    className="child-modal-btn-cancel"
                    onClick={onClose}
                  >
                    CANCEL
                  </button>
                  <button
                    className={`child-modal-btn-save ${isEditMode && !hasFormChanges ? 'disabled' : ''}`}
                    onClick={handleSave}
                    disabled={isEditMode && !hasFormChanges}
                  >
                    SAVE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ChildInformationModal;
